import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Pages
import NewLanding from "@/pages/NewLanding";
import Dashboard from "@/pages/Dashboard";
import DocumentProcessor from "@/pages/DocumentProcessor";
import SmartEditor from "@/pages/SmartEditor";
import Analytics from "@/pages/Analytics";
import ComplianceHub from "@/pages/ComplianceHub";
import AccountSecurity from "@/pages/AccountSecurity";
import AdminSecurityDashboard from "@/pages/AdminSecurityDashboard";
import APIKeysManager from "@/pages/APIKeysManager";
import DocumentPipelineVisualization from "@/pages/DocumentPipelineVisualization";
import HeroDemo from "@/pages/HeroDemo";
import PlaybookList from "@/pages/PlaybookList";
import PlaybookDetails from "@/pages/PlaybookDetails";
import WorkflowDashboard from "@/pages/WorkflowDashboard";
import AuditBundles from "@/pages/AuditBundles";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem("standari_token");
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data.user);
      } catch (error) {
        localStorage.removeItem("standari_token");
      }
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("standari_token", response.data.access_token);
      setUser(response.data.user);
      toast.success("Welcome back!");
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
      return false;
    }
  };

  const register = async (email, username, password) => {
    try {
      const response = await axios.post(`${API}/auth/register`, { email, username, password });
      localStorage.setItem("standari_token", response.data.access_token);
      setUser(response.data.user);
      toast.success("Account created successfully!");
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem("standari_token");
    setUser(null);
    toast.info("Logged out");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
        <div className="text-xl text-slate-600">Loading...</div>
      </div>
    );
  }

  return (
    <>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={user ? <Navigate to="/dashboard" /> : <NewLanding login={login} register={register} />} />
          <Route path="/dashboard" element={user ? <Dashboard user={user} logout={logout} /> : <Navigate to="/" />} />
          <Route path="/process" element={user ? <DocumentProcessor user={user} logout={logout} /> : <Navigate to="/" />} />
          <Route path="/editor/:contentId" element={user ? <SmartEditor user={user} logout={logout} /> : <Navigate to="/" />} />
          <Route path="/analytics" element={user ? <Analytics user={user} logout={logout} /> : <Navigate to="/" />} />
          <Route path="/compliance" element={user ? <ComplianceHub user={user} logout={logout} /> : <Navigate to="/" />} />
          <Route path="/account/security" element={user ? <AccountSecurity user={user} logout={logout} /> : <Navigate to="/" />} />
          <Route path="/admin/security" element={user ? <AdminSecurityDashboard user={user} logout={logout} /> : <Navigate to="/" />} />
          <Route path="/account/api-keys" element={user ? <APIKeysManager user={user} logout={logout} /> : <Navigate to="/" />} />
          <Route path="/how-it-works" element={user ? <DocumentPipelineVisualization user={user} logout={logout} /> : <Navigate to="/" />} />
          <Route path="/demo" element={user ? <HeroDemo user={user} logout={logout} /> : <Navigate to="/" />} />
          <Route path="/playbooks" element={user ? <PlaybookList user={user} logout={logout} /> : <Navigate to="/" />} />
          <Route path="/playbooks/:playbookId" element={user ? <PlaybookDetails user={user} logout={logout} /> : <Navigate to="/" />} />
          <Route path="/workflows/:workflowId" element={user ? <WorkflowDashboard user={user} logout={logout} /> : <Navigate to="/" />} />
          <Route path="/audit-bundles" element={user ? <AuditBundles user={user} logout={logout} /> : <Navigate to="/" />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </>
  );
}

export default App;